/* tslint:disable */

import { Injectable, Injector } from '@angular/core';
import { Location } from "@angular/common";
import { TwoDService } from "../views/two-dview/two-d.service";
import { WebGLService } from "../views/three-dview/web-gl.service";
import { MarkersService } from "../markers/markers.service";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { AtlasSharedService } from "../atlas-shared.service";
import { TabService } from "../../tab.service";
import { HttpService } from "../../http.service";
import { environment } from "../../../environments/environment";
import { SelectedPosition } from "../views/two-dview/twoDViewModels";
import { Vector3 } from "three";
import { AlertService } from "../../alert.service";
import { NgxSpinnerService } from "ngx-spinner";
import { RegionsService } from "../regions/regions.service";
import { BehaviorSubject } from "rxjs";
import { LayoutEventsService } from "../views/two-dview/two-dview-layout/layout-events.service";
import { NeuronsService } from "../neurons/neurons.service";
import { ShapeROIsService } from "../ROIs/shape-rois.service";
import { Sharing3DViewData } from '../sharing-3d-view-data.model';
import { StatusMessageService } from "../status-message.service";
import { skip, skipWhile } from 'rxjs/operators';
import { UserService } from '../user-management/user.service';

@Injectable({
    providedIn: 'root'
})
export class LayoutService {
    //The _currentViewControlsData$ BehaviorSubject will be refreshed once receiving data from the backend - Shared current view.
    private _currentViewControlsData$ = new BehaviorSubject<Sharing3DViewData>(this.getDefaultShareView3dSettings());
    // The currentViewControlsData$ is a getter method that provides read-only access to the _currentViewControlsData$ behavior subject
    currentViewControlsData$ = this._currentViewControlsData$.asObservable();

    urlSharedViewData = environment.apiURL + 'load_shared_view_data';
    urlNotifyUserOfNewFeature = environment.apiURL + 'notify_user_of_new_feature';
    urlGetItemsCount = environment.apiURL + 'get_items_count';

    currentActiveView = '';

    viewOptions = {
        twoD: true,
        threeD: false,
        displayTwoD: '2D',
        displayThreeD: '3D'
    }
    showShareViewComment = false;

    isDataBeingLoaded = false; // The data related to loading current view

    constructor(private location: Location,
        private twoDService: TwoDService,
        private spinner: NgxSpinnerService,
        private threeDService: WebGLService,
        private markersService: MarkersService,
        private injector: Injector,
        private atlasSharedService: AtlasSharedService,
        private tabService: TabService,
        private router: Router,
        private route: ActivatedRoute,
        private httpService: HttpService,
        private alertService: AlertService,
        private userService: UserService,
        private statusMessageService: StatusMessageService) {
        router.events.subscribe((routerEvent) => {
            this.checkRouterEvent(routerEvent);
        });
        this.currentActiveView = this.viewOptions.displayTwoD;
        this.retrieveHashFragmentFromUrl()

        this.userService.subjectIsUserAuthenticated
            .pipe(
                // Don’t emit until the value is no longer null
                skipWhile(val => val === null),
                // Skip the *very first* real emission (the “initial” state)
                skip(1)
            )
            .subscribe(isAuthenticated => {

                this.checkForItemsCount();
                this.checkForInitialMarkersData();
            });
      
    }

    private getDefaultShareView3dSettings(): Sharing3DViewData {
        let rotations = this.atlasSharedService.sharing3DViewData.rotations;
        let sceneSettings = this.atlasSharedService.sharing3DViewData.sceneSettings;
        let brainModelSettings = this.atlasSharedService.sharing3DViewData.brainModelSettings;
        let sceneClippingSettings = this.atlasSharedService.sharing3DViewData.sceneClippingSettings;
        let brainClippingSettings = this.atlasSharedService.sharing3DViewData.brainClippingSettings;

        return {
            rotations: { ...rotations },
            sceneSettings: { ...sceneSettings },
            brainModelSettings: { ...brainModelSettings },
            sceneClippingSettings: { ...sceneClippingSettings },
            brainClippingSettings: { ...brainClippingSettings }
        };
    }

    ovViewSwitch() {
        let currentURL = this.location.path(false);
        let newURL = ''
        if (currentURL.includes('2d')) {
            newURL = '/atlas/3d'

            this.setViewModeOptions(false, true, this.viewOptions.displayThreeD);
            this.threeDService.resize();
        } else {
            this.tabService.openMarkersTab();
            newURL = '/atlas/2d'

            this.setViewModeOptions(true, false, this.viewOptions.displayTwoD);
            this.twoDService.onResize();
        }
        this.atlasSharedService.onHidePanelMovementOptions()
        this.location.replaceState(newURL);
    }

    // This function will be invoked upon requesting the page from the links /2d or /3d, or upon clicking 'Launch Atlas'.
    checkRouterEvent(routerEvent) {
        if (routerEvent instanceof NavigationEnd) {

            this.notifyUserOfNewFeatures();

            this.checkForInitialMarkersData();

            // Herwig requested to display the count of neurons, regions, and markers beside each panel on the left side menu.
            this.checkForItemsCount()

            if (routerEvent.url.includes('2d')) {
                this.tabService.openMarkersTab();
                this.setViewModeOptions(true, false, this.viewOptions.displayTwoD);
                setTimeout(() => {
                    this.twoDService.onResize();
                }, 2000);
            } else if (routerEvent.url.includes('3d')) {
                this.setViewModeOptions(false, true, this.viewOptions.displayThreeD);
            }
        }
    }

    setShareView2DSettings(response) {
        let selectedPosition = response['data']['settings']['twoD']['selected_position_2d'];
        //Set the new position related to all the views.
        SelectedPosition.initializeOrthogonalViews(new Vector3(selectedPosition['x'], selectedPosition['y'], selectedPosition['z']), this.atlasSharedService.sharingCurrentViewStatus.hash);


        //Set the chosen active layout (orthogonal views, single/split layout(dorsal, sagittal,coronal, Movie X&Y))
        this.twoDService.onActiveViewClick(response['data']['settings']['twoD']['chosen_view_key']);

        let eventService = this.injector.get<LayoutEventsService>(LayoutEventsService);
        eventService.onMouseDown(null, null, this.twoDService.activeView);
    }

    getShareView3DSettingsFromBackend(response) {
        const settings = response['data']['settings']['threeD'];
        /* const {camera_position, camera_up_position} = settings,
           rotations: {
                position: camera_position,
                up: camera_up_position
            },
            the previous 3 lines comment is  a short way of expressing this later:
            rotations: {
                position: settings['camera_position'],
                up: settings['camera_up_position']
            },*/
        const { camera_position, camera_up_position } = settings;
        const { scene_settings, brain_model_settings, scene_clipping_settings, brain_clipping_settings } = settings;

        let currentViewControlsData = <Sharing3DViewData>({
            rotations: {
                position: camera_position,
                up: camera_up_position
            },
            sceneSettings: {
                sceneBackgroundColor: scene_settings.scene_background_color,
                directionalLightSelected: scene_settings.directional_light_selected,
                ambientLightSelected: scene_settings.ambient_light_selected,
                ambientLightBackgroundColor: scene_settings.ambient_light_background_color,
                directionalLightBackgroundColor: scene_settings.directional_light_background_color
            },
            brainModelSettings: {
                hasOutline: brain_model_settings.has_outline,
                hasFibers: brain_model_settings.has_fibers,
                hasCellBodies: brain_model_settings.has_cell_bodies,
                outlineOpacity: brain_model_settings.outline_opacity,
                fibersOpacity: brain_model_settings.fibers_opacity,
                cellBodiesOpacity: brain_model_settings.cell_bodies_opacity
            },
            sceneClippingSettings: { ...scene_clipping_settings },
            brainClippingSettings: { ...brain_clipping_settings }
        });
        this._currentViewControlsData$.next(currentViewControlsData);
    }

    //This function will load markers first, then Regions , then Neurons, then ROIs
    async mySequentialTasksOfGettingCurrentViewData(response) {
        this.updateSharCurrentViewScene();
        await this.markersService.loadSelectedMarkersShareView(response['data']['markers']);

        let neuronsGroupService = this.injector.get<NeuronsService>(NeuronsService);
        await neuronsGroupService.loadSelectedNeuronsGroupsShareView(response['data']['neurons_group']);

        let regionsService = this.injector.get<RegionsService>(RegionsService);
        await regionsService.loadSelectedRegionsShareView(response['data']['regions'])

        let roisService = this.injector.get<ShapeROIsService>(ShapeROIsService);
        await roisService.loadSelectedROIsShareView(response['data']['rois'])
    }

    async loadCurrentView() {

        console.log("......................:::::::::loadCurrentView:::::::::....................");
        await this.statusMessageService.updateStatus("Start fetching data", true, true, 2000);
        this.atlasSharedService.sharedViewItems.isLoading = false;
        // retrieve the markers associated with the sharing current view from the DB
        this.httpService.postRequest(this.urlSharedViewData, { hash: this.atlasSharedService.sharingCurrentViewStatus.hash }).subscribe(async response => {
            if (response.success) {
                if (!!response['data']['neurons_group']['groups_names'].length) {
                    this.atlasSharedService.sharedViewItems.neurons.hasSelectedNeurons = true;
                }

                this.setShareView2DSettings(response);
                this.getShareView3DSettingsFromBackend(response);

                // If there is a comment to be shared with others.
                if (!!response['data']['settings']['comment'] && !this.showShareViewComment) {
                    this.alertService.showTopSuccessMessage("Comment from the creator of this view:", response['data']['settings']['comment']);
                    //This flag to stop appearing the comment everytime the user switch between 2D/3D
                    this.showShareViewComment = true;
                }

                await this.mySequentialTasksOfGettingCurrentViewData(response);

                this.atlasSharedService.sharedViewItems.isLoading = true;

                this.updateSharCurrentViewScene();

                this.atlasSharedService.updateScene(1000);
                this.isDataBeingLoaded = true;

                response.report && this.alertService.showWarningMsg("Unfortunately, with the change, replacement or deletion of some items from the atlas, this link may become inconsistent. Therefore, please note the following changes: \n " + response.report);

            } else {
                this.alertService.showErrorMsg('Something went wrong with loading markers. Please try gain!.');
            }
        },
            error => {
                this.alertService.showErrorMsg('Something went wrong with loading markers. Please try gain!.');
                this.spinner.hide();
            });
    }

    checkForInitialMarkersData() {
        const hash = this.atlasSharedService.sharingCurrentViewStatus.hash;
    
        this.spinner.show();
        this.markersService.resetInitialMarkersData();
        this.markersService.deselectAllMarkers();
        this.markersService.loadInitialMarkersData()
            .then(() => {
                if (!hash) {

                    return this.markersService.selectInitialMarkers();
                } else if (hash.indexOf('_') === -1) {
                    if (!this.isDataBeingLoaded) {
                        this.loadCurrentView();
                    }
                }
            })
            .catch(err => {
                console.error("Error during marker loading workflow:", err);
            })
            .finally(() => {
                this.spinner.hide();
            });
    }
    

    retrieveHashFragmentFromUrl() {
        this.route.fragment.subscribe((fragment: string) => {
            this.atlasSharedService.sharingCurrentViewStatus.hash = fragment;
        })
    }

    setViewModeOptions(twoDValue, threeDValue, currentActiveView) {
        this.viewOptions.twoD = twoDValue;
        this.viewOptions.threeD = threeDValue;
        this.currentActiveView = currentActiveView;
    }

    notifyUserOfNewFeatures() {
        this.httpService.postRequest(this.urlNotifyUserOfNewFeature, {})
            .subscribe(response => {
                if (response.success) {
                    if (response.data.tasks.length !== 0) {
                        this.alertService.showNewFeaturesNotifications(response.data.tasks, response.data.features_to_notify, "atlas");
                    }
                }
            },
                error => {
                    console.error(error);
                })
    }

    checkForItemsCount() {
        this.httpService.postRequest(this.urlGetItemsCount, {}).subscribe(async response => {
            if (response.success) {
                this.atlasSharedService.itemsCount.markers = response.markersCount;
                this.atlasSharedService.itemsCount.neurons = response.neuronsCount;
                this.atlasSharedService.itemsCount.regions = response.regionCount;
            }
            // this.spinner.hide();
        });
    }

    updateSharCurrentViewScene() {
        let layoutEventsService = this.injector.get<LayoutEventsService>(LayoutEventsService);

        layoutEventsService.onRangeStepButtonClick(this.twoDService.views.dorsal, 1);
        layoutEventsService.onRangeStepButtonClick(this.twoDService.views.sagittal, 1);
        layoutEventsService.onRangeStepButtonClick(this.twoDService.views.coronal, 1);

        layoutEventsService.onRangeStepButtonClick(this.twoDService.views.dorsal, -1);
        layoutEventsService.onRangeStepButtonClick(this.twoDService.views.sagittal, -1);
        layoutEventsService.onRangeStepButtonClick(this.twoDService.views.coronal, -1);

    }

}
